<script setup>
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import LoadingSpinner from "../../components/LoadingSpinner.vue";
import { scopeSections, toCurrency } from "../../assets/js/helpers";
import TradesFilter from "./TradesFilter.vue";

const props = defineProps({
  propertyId: {
    type: [String, Number],
    required: true,
  },
  propertyMetadata: {
    type: Object,
    default: () => ({}),
  },
  setActions: {
    type: Function,
  },
});

const loading = ref(false);
const scopeObj = ref({});
const activeView = ref("overview");
const selectedTrades = ref([]);
const viewMoreState = ref({});

const store = useStore();
const toast = useToast();

const apiEndpoint = store.state.apiEndpoint;
const token = sessionStorage.getItem("token");
const headers = {
  headers: {
    Authorization: `Bearer ${token}`,
  },
};

const NO_TRADE_KEY = "no-trade";
const NO_TRADE_LABEL = "No Trades Assigned";
const ITEMS_LIMIT = 10;

const cleanupTextFormat = (text) => {
  if (!text) return "";
  return text
    .replace(/[\n\r]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const sectionTitleMap = computed(() => {
  const map = {};
  scopeSections.forEach((section) => {
    map[section.id] = section.title;
  });

  if (props.propertyMetadata?.sections) {
    Object.entries(props.propertyMetadata.sections).forEach(([id, section]) => {
      map[id] = section.schedule2_title || section.title || map[id] || id;
    });
  }
  return map;
});

const subsectionTitleMap = computed(() => {
  const map = {};
  scopeSections.forEach((section) => {
    (section.items || []).forEach((item) => {
      map[item.id] = item.title || item.schedule2_title || item.id;
    });
  });

  if (props.propertyMetadata?.sections) {
    Object.values(props.propertyMetadata.sections).forEach((section) => {
      (section.items || []).forEach((item) => {
        map[item.id] = item.title || item.schedule2_title || item.id;
      });
    });
  }

  return map;
});

const getScope = async () => {
  loading.value = true;
  try {
    const response = await axios.get(
      `${apiEndpoint}/property/get-scope/${props.propertyId}`,
      headers,
    );

    if (Object.keys(response.data.scope || {}).length !== 0) {
      const cleanedScope = {};
      Object.entries(response.data.scope).forEach(([sectionId, section]) => {
        cleanedScope[sectionId] = {
          ...section,
          items: (section.items || []).map((item) => ({
            ...item,
            item: cleanupTextFormat(item.item),
          })),
        };
      });
      scopeObj.value = cleanedScope;
    } else {
      scopeObj.value = {};
    }
  } catch (error) {
    toast.error("Failed to retrieve scope data");
    console.error("Error:", error);
  } finally {
    loading.value = false;
  }
};

const flatItems = computed(() => {
  const items = [];
  Object.entries(scopeObj.value || {}).forEach(([sectionId, section]) => {
    (section.items || []).forEach((item) => {
      if (item?.is_room) return;
      items.push({
        ...item,
        sectionId,
      });
    });
  });
  return items;
});

const tradeGroups = computed(() => {
  const map = new Map();

  const addItemToGroup = (codeVal, label, item) => {
    const key = codeVal ?? NO_TRADE_KEY;
    if (!map.has(key)) {
      map.set(key, {
        key,
        tradeCodeValue: codeVal,
        tradeCodeLabel: label,
        items: [],
      });
    }
    map.get(key).items.push(item);
  };

  flatItems.value.forEach((item) => {
    const codes = Array.isArray(item.trade_code)
      ? item.trade_code.filter(Boolean)
      : item.trade_code
        ? [item.trade_code]
        : [];

    if (!codes.length) {
      addItemToGroup(null, NO_TRADE_LABEL, item);
      return;
    }

    codes.forEach((code) => addItemToGroup(code, code, item));
  });

  const groups = Array.from(map.values()).map((group) => ({
    ...group,
    total: group.items.reduce((sum, curr) => {
      if (
        curr.trade_costs &&
        curr.trade_costs["" + group.tradeCodeValue] !== undefined
      ) {
        return sum + (Number(curr.trade_costs[group.tradeCodeValue]) || 0);
      }
      // fallback to legacy cost when no trade-specific value
      return sum + (Number(curr.cost) || 0);
    }, 0),
  }));

  groups.sort((a, b) => {
    const aNoTrade = a.tradeCodeValue === null;
    const bNoTrade = b.tradeCodeValue === null;
    if (aNoTrade && !bNoTrade) return 1;
    if (!aNoTrade && bNoTrade) return -1;
    return a.tradeCodeLabel.localeCompare(b.tradeCodeLabel);
  });

  return groups;
});

const tradeFilterOptions = computed(() => {
  return tradeGroups.value.map((group) => ({
    label: group.tradeCodeLabel,
    value: group.key,
  }));
});

const filteredGroups = computed(() => {
  if (!selectedTrades.value.length) return tradeGroups.value;
  return tradeGroups.value.filter((group) =>
    selectedTrades.value.includes(group.key),
  );
});

const showingAllTrades = computed(
  () =>
    selectedTrades.value.length === 0 ||
    selectedTrades.value.length === tradeGroups.value.length,
);

const selectedTradeLabels = computed(() => {
  const byKey = new Map(
    tradeGroups.value.map((group) => [group.key, group.tradeCodeLabel]),
  );
  return selectedTrades.value.map((key) => byKey.get(key) || key);
});

const selectedFilterText = computed(() =>
  showingAllTrades.value
    ? "All trades"
    : selectedTradeLabels.value.length
      ? selectedTradeLabels.value.join(", ")
      : "Selected trades",
);

const summaryLabel = computed(() =>
  showingAllTrades.value ? "TOTAL (Exc. GST)" : "FILTERED TOTAL (Exc. GST)",
);

const filteredGroupsTotal = computed(() =>
  filteredGroups.value.reduce(
    (sum, group) => sum + (Number(group.total) || 0),
    0,
  ),
);

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const printTotalsTable = () => {
  const rowsHtml = filteredGroups.value.length
    ? filteredGroups.value
        .map(
          (group) => `
          <tr>
            <td>${escapeHtml(group.tradeCodeLabel)}</td>
            <td class="text-end">${escapeHtml(toCurrency(group.total))}</td>
          </tr>
        `,
        )
        .join("")
    : `<tr><td colspan="2" class="text-center">No trades found for this filter.</td></tr>`;

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    toast.error(
      "Unable to open print window. Please allow pop-ups and try again.",
    );
    return;
  }

  const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Scope Trades Totals</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 24px;
            color: #212529;
          }
          h1 {
            margin: 0 0 4px 0;
            font-size: 20px;
          }
          .meta {
            margin-bottom: 14px;
            color: #6c757d;
            font-size: 12px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #dee2e6;
            padding: 10px 12px;
            font-size: 13px;
          }
          thead th, tfoot th {
            background: #f8f9fa;
            font-weight: 700;
          }
          .text-end {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .print-hint {
            margin-top: 12px;
            font-size: 12px;
            color: #6c757d;
          }
          .print-btn {
            border: 1px solid #adb5bd;
            background: #fff;
            color: #212529;
            padding: 6px 10px;
            border-radius: 4px;
            cursor: pointer;
          }
          @media print {
            .print-hint {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <h1>Scope Trades Totals</h1>
        <div class="meta">
          Property ID: ${escapeHtml(props.propertyId)} |
          Filter: ${escapeHtml(selectedFilterText.value)}
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:70%">Trade</th>
              <th class="text-end" style="width:30%">Total</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
          <tfoot>
            <tr>
              <th>${escapeHtml(summaryLabel.value)}</th>
              <th class="text-end">${escapeHtml(toCurrency(filteredGroupsTotal.value))}</th>
            </tr>
          </tfoot>
        </table>
        <div class="print-hint">
          If the print dialog does not open automatically, click
          <button class="print-btn" onclick="window.print()">Print</button>.
        </div>
        <script>
          window.addEventListener('load', function () {
            setTimeout(function () {
              try {
                window.focus();
                window.print();
              } catch (e) {}
            }, 200);
          });
          window.addEventListener('afterprint', function () {
            window.close();
          });
        <\/script>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
};

const getSectionLabel = (item) => {
  const base = sectionTitleMap.value[item.sectionId] || item.sectionId;
  if (item.subitem && subsectionTitleMap.value[item.subitem]) {
    return `${base} — ${subsectionTitleMap.value[item.subitem]}`;
  }
  return base;
};

const visibleItems = (group) => {
  if (viewMoreState.value[group.key]) return group.items;
  return group.items.slice(0, ITEMS_LIMIT);
};

const toggleViewMore = (key) => {
  viewMoreState.value[key] = !viewMoreState.value[key];
};

onMounted(async () => {
  await getScope();
  if (props.setActions) {
    props.setActions({
      save: () => {},
      reset: () => {},
      print: printTotalsTable,
    });
  }
});

watch(
  () => props.propertyId,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      selectedTrades.value = [];
      viewMoreState.value = {};
      await getScope();
    }
  },
);
</script>

<template>
  <LoadingSpinner
    v-if="loading"
    message="Loading scope..."
    :fullScreen="true"
  />

  <div class="trades-totals">
    <div
      class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3"
    >
      <div class="d-flex flex-column">
        <h5 class="mb-0 fwt-bold">Scope Trades Totals</h5>
        <p class="text-muted mb-0">Overview or drill down by trade code.</p>
      </div>
      <div
        class="d-flex align-items-center gap-2 flex-wrap justify-content-md-end"
      >
        <div class="d-flex gap-3">
          <TradesFilter
            class="filter-compact"
            :options="tradeFilterOptions"
            v-model="selectedTrades"
          />

          <div
            class="btn-group view-toggle flex-shrink"
            role="group"
            aria-label="View switch"
          >
            <button
              type="button"
              class="btn"
              :class="
                activeView === 'overview'
                  ? 'btn-primary'
                  : 'btn-outline-primary'
              "
              @click="activeView = 'overview'"
            >
              Overview
            </button>
            <button
              type="button"
              class="btn"
              :class="
                activeView === 'items' ? 'btn-primary' : 'btn-outline-primary'
              "
              @click="activeView = 'items'"
            >
              Items breakdown
            </button>
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="printTotalsTable"
          >
            <i class="fas fa-print me-2"></i>Print totals
          </button>
        </div>
      </div>
    </div>

    <!-- Overview Table -->
    <div v-if="activeView === 'overview'" class="card shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 70%">Trade</th>
                <th class="text-end" style="width: 30%">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="group in filteredGroups" :key="group.key">
                <td class="fw-semibold">{{ group.tradeCodeLabel }}</td>
                <td class="text-end">{{ toCurrency(group.total) }}</td>
              </tr>
              <tr v-if="filteredGroups.length === 0">
                <td colspan="2" class="text-center text-muted py-3">
                  No trades found for this filter.
                </td>
              </tr>
            </tbody>
            <tfoot class="table-light">
              <tr>
                <th class="fw-bold">{{ summaryLabel }}</th>
                <th class="text-end fw-bold">
                  {{ toCurrency(filteredGroupsTotal) }}
                </th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Items breakdown -->
    <div v-else>
      <div v-if="!filteredGroups.length" class="alert alert-info">
        No scope items available for this property.
      </div>

      <div
        v-for="group in filteredGroups"
        :key="group.key"
        class="card shadow-sm mb-4"
      >
        <div class="card-header trade-grid">
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-dark px-3 py-2 trade-title">{{
              group.tradeCodeLabel
            }}</span>
            <span class="text-muted small"
              >{{ group.items.length }} item{{
                group.items.length === 1 ? "" : "s"
              }}</span
            >
          </div>
          <div></div>
          <div class="text-end fwt-bold cost-col total-align">
            {{ toCurrency(group.total) }}
          </div>
        </div>

        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped align-middle mb-0 trades-table">
              <colgroup>
                <col style="width: var(--col-item)" />
                <col style="width: var(--col-section)" />
                <col style="width: var(--col-cost)" />
              </colgroup>
              <thead class="table-light">
                <tr>
                  <th class="item-col">Item</th>
                  <th class="section-col">Section</th>
                  <th class="cost-col text-end">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, idx) in visibleItems(group)"
                  :key="`${group.key}-${idx}`"
                >
                  <td class="item-col">
                    <div class="fw-semibold">
                      {{ item.item || "Untitled item" }}
                    </div>
                    <div v-if="item.description" class="text-muted small">
                      {{ item.description }}
                    </div>
                  </td>
                  <td class="section-col">{{ getSectionLabel(item) }}</td>
                  <td class="cost-col text-end">
                    {{
                      toCurrency(
                        item.trade_costs &&
                          group.tradeCodeValue in item.trade_costs
                          ? item.trade_costs[group.tradeCodeValue]
                          : item.cost,
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="group.items.length > ITEMS_LIMIT" class="text-center py-3">
            <button
              class="btn btn-outline-primary btn-sm"
              @click="toggleViewMore(group.key)"
            >
              {{ viewMoreState[group.key] ? "View less" : "View more" }}
            </button>
          </div>
        </div>

        <div class="card-footer trade-grid fwt-bold">
          <div></div>
          <div></div>
          <span class="cost-col text-end total-align">{{
            toCurrency(group.total)
          }}</span>
        </div>
      </div>

      <div class="card shadow-sm border summary-card">
        <div
          class="card-body d-flex justify-content-between align-items-center py-3"
        >
          <span class="fwt-bold">{{ summaryLabel }}</span>
          <span class="fwt-bold">{{ toCurrency(filteredGroupsTotal) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.trades-totals {
  min-height: 200px;
  width: 100%;
  margin: 0;
  --col-item: 60%;
  --col-section: 20%;
  --col-cost: 20%;
}

.filter-select {
  min-width: 240px;
}

.card-header {
  background-color: #f8f9fa;
}

.badge.bg-dark {
  letter-spacing: 0.03em;
}

.trade-title {
  font-size: 1.2rem;
}

.total-align {
  margin-left: auto;
}

.view-toggle .btn {
  min-width: 140px;
  white-space: nowrap;
}

.trades-table {
  table-layout: fixed;
}

.trades-table th,
.trades-table td {
  vertical-align: top;
}

.filter-compact {
  width: 280px;
  min-width: 240px;
}

.item-col {
  width: var(--col-item);
  overflow-wrap: anywhere;
  white-space: normal;
}

.section-col {
  width: var(--col-section);
  min-width: 160px;
  overflow-wrap: anywhere;
  white-space: normal;
}

.cost-col {
  width: var(--col-cost);
  min-width: 140px;
  padding-right: 12px;
}

.trade-grid {
  display: grid;
  grid-template-columns: var(--col-item) var(--col-section) var(--col-cost);
  align-items: center;
  gap: 0.5rem;
}

.summary-card {
  border-color: #dee2e6;
}
</style>
